import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/context/auth-context';
import { AlertCircle, Download, Loader2, Send, Webhook } from 'lucide-react';
import {
  AuthInput,
  DeliveryLogEntry,
  FullExportRun,
  LogFilter,
  WebhookApiError,
  WebhookDelivery,
  saveFile,
  webhookDeliveryApi,
} from '@/services/webhookDelivery';
import { SCHEMA_FILE_NAME, buildIntegrationGuide, guideFileName, renderGuideMarkdown, schemaLabel } from './webhookIntegrationGuide';
import type { GuideIdentity } from './webhookIntegrationGuide';

/**
 * Feed Export › Webhook (issue #7222; the vault's webhook delivery PRD, rev. 5.2, "Configuration").
 *
 * One HTTPS endpoint per store; PageMind POSTs one product per call: the changes every hour, and a full export when the
 * user requests one. The words are bucket phase 2.4's: "full export", "changes"; the button is "Request full export".
 * Everything the tab shows comes from GET /webhook-delivery: the credential value never reaches the browser after it
 * was typed (the header name and its last 4 characters only).
 *
 * The operator alerts of the same failures (Ops Console, operators' e-mail) are for PageMind operators only: the link
 * to them is shown to super admins, never to the merchant.
 */

const POLL_MS = 5000;        // something is moving: calls queued or sending, a full export, a test
const IDLE_POLL_MS = 60000;  // nothing pending: still notice the next hourly changes

// rev. 5.6 part 1 ("Only send canonicals"; vault rev-5.6-canonicals-and-final-rejections.md): offered only on a store
// whose canonical aggregator is enabled (status `canonicalsAvailable`), set at Enable and immutable after it
export const CANONICALS_LABEL = 'Only send canonicals (one call per model)';
export const CANONICALS_HELP = 'Send one call per model, with the model\'s representative product, instead of one call per variant. Use it if your system stores generated content per model. A product without a model is its own model.';
/** The Configuration table's words for an endpoint's granularity (the backend's audit event says the same). */
export const granularityText = (canonicalsOnly: boolean | undefined) => (canonicalsOnly ? 'one call per model (canonicals only)' : 'one call per product');

// rev. 5.7 (vault rev-5.7-request-timeout-setting.md): the response timeout, a per-endpoint setting edited like the pace
export const timeoutHelp = (min: number, max: number) => `How long we wait for your endpoint to answer each call (${min}–${max} s). A call that takes longer counts as no answer: it is retried and, after enough of them, the webhook pauses and we are alerted. A change applies to the next call.`;
/** The bounds sentence, word for word the backend's refusal (validateTimeout). */
export const timeoutBounds = (min: number, max: number) => `Response timeout is a whole number of seconds from ${min} to ${max}.`;
/** A typed value the backend would accept: a whole number of seconds within the bounds. */
export function timeoutValid(value: string, min: number, max: number): boolean {
  if (!/^\d+$/.test(value.trim())) return false;
  const n = Number(value);
  return n >= min && n <= max;
}

export const WHAT_YOU_GET ='What you get: your endpoint receives one HTTPS POST per product, with the product exactly as the export API returns it. Changes are sent every hour; a full export sends every product of this store, and runs only when you request one.';

function formatDate(value?: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleString();
}

function fmt(n: number | null | undefined): string {
  return (n ?? 0).toLocaleString();
}

/** How often to refresh: quickly while anything is in progress, slowly otherwise; never for a store without a webhook. */
export function pollDelay(w: WebhookDelivery | null): number | null {
  if (!w || w.status === 'not_enabled' || w.status === 'disabled') return null;
  const q = w.queue;
  const busy = (q && (q.pending > 0 || q.inFlight > 0))
    || Boolean(w.fullExport && (w.fullExport.requestedAt || (w.fullExport.run && ['queued', 'running', 'failed_retryable'].includes(w.fullExport.run.status))))
    || Boolean(w.lastTest && (w.lastTest.status === 'pending' || w.lastTest.status === 'in_flight'));
  return busy ? POLL_MS : IDLE_POLL_MS;
}

/** The pause, in the merchant's words, with what to do. */
export function pauseSentence(w: WebhookDelivery): string | null {
  if (w.status !== 'paused' || !w.pause) return null;
  if (w.pause.reason === 'auth_rejected') {
    return `Paused automatically: your endpoint refused our authentication${w.pause.detail ? ` (${w.pause.detail})` : ''}. Replace the authentication, then resume.`;
  }
  if (w.pause.reason === 'circuit_open') {
    return 'Paused automatically: your endpoint has not accepted a call for 24 hours. Fix the endpoint, then resume: we send one call first, and nothing queued is lost.';
  }
  return 'Paused. Calls are queued and sent when you resume; nothing is skipped.';
}

function AuthFields({ value, onChange, idPrefix }: { value: AuthInput; onChange: (v: AuthInput) => void; idPrefix: string }) {
  return (
    <div className="space-y-3">
      <RadioGroup
        value={value.type}
        onValueChange={(t) => onChange(t === 'basic' ? { type: 'basic', username: '', password: '' } : { type: 'header', name: 'Authorization', value: '' })}
        className="flex gap-6"
      >
        <div className="flex items-center gap-2">
          <RadioGroupItem value="header" id={`${idPrefix}-type-header`} />
          <Label htmlFor={`${idPrefix}-type-header`}>Header</Label>
        </div>
        <div className="flex items-center gap-2">
          <RadioGroupItem value="basic" id={`${idPrefix}-type-basic`} />
          <Label htmlFor={`${idPrefix}-type-basic`}>Username and password (Basic)</Label>
        </div>
      </RadioGroup>
      {value.type === 'header' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label htmlFor={`${idPrefix}-name`}>Header name</Label>
            <Input id={`${idPrefix}-name`} value={value.name} placeholder="Authorization" autoComplete="off"
              onChange={(e) => onChange({ ...value, name: e.target.value })} />
          </div>
          <div className="space-y-1">
            <Label htmlFor={`${idPrefix}-value`}>Header value</Label>
            <Input id={`${idPrefix}-value`} type="password" value={value.value} placeholder="Bearer …" autoComplete="new-password"
              onChange={(e) => onChange({ ...value, value: e.target.value })} />
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label htmlFor={`${idPrefix}-username`}>Username</Label>
            <Input id={`${idPrefix}-username`} value={value.username} autoComplete="off"
              onChange={(e) => onChange({ ...value, username: e.target.value })} />
          </div>
          <div className="space-y-1">
            <Label htmlFor={`${idPrefix}-password`}>Password</Label>
            <Input id={`${idPrefix}-password`} type="password" value={value.password} autoComplete="new-password"
              onChange={(e) => onChange({ ...value, password: e.target.value })} />
          </div>
        </div>
      )}
      <p className="text-xs text-muted-foreground">PageMind stores the value encrypted and shows only its last 4 characters again.</p>
    </div>
  );
}

function callStatusText(d: DeliveryLogEntry): string {
  switch (d.status) {
    case 'delivered': return 'Delivered';
    case 'in_flight': return 'Sending';
    case 'pending': return d.attempts > 0 && d.nextAttemptAt ? `Waiting (retry at ${formatDate(d.nextAttemptAt)})` : 'Waiting';
    case 'held': return `Held: ${d.error || 'it failed the payload contract check'}`;
    case 'skipped': return `Skipped: ${d.error || 'not exportable'}`;
    case 'failed': return `Failed: ${d.error || 'rejected by your endpoint'}`;
    case 'superseded': return 'Replaced by a newer call of this product (sent with its current content)';
    case 'disabled': return 'Not made: the webhook was disabled';
    default: return d.status;
  }
}

const LOG_FILTERS: [LogFilter, string][] = [['all', 'All'], ['failed', 'Failed'], ['held', 'Held'], ['skipped', 'Skipped']];
const EMPTY_LOG: Record<LogFilter, string> = {
  all: 'No calls yet.', failed: 'No failed calls.', held: 'No held products.', skipped: 'No skipped products.',
  pending: 'Nothing waiting.', delivered: 'Nothing delivered yet.', closed: 'No replaced or cancelled calls.',
};

/** rev. 5.6: what a count counts — models on a canonicals-only endpoint (one call per model), products otherwise. */
function unitOf(canonicalsOnly: boolean | undefined) {
  return (n: number) => (canonicalsOnly ? (n === 1 ? 'model' : 'models') : (n === 1 ? 'product' : 'products'));
}

/** The full export's progress, from its outcome ledger (PRD "Request full export": delivered, held, skipped, failed, pending). */
function progressText(run: FullExportRun): string {
  const parts = [`${fmt(run.delivered)} delivered`, `${fmt(run.held)} held`, `${fmt(run.skipped)} skipped`, `${fmt(run.failed)} failed`, `${fmt(run.pending)} pending`];
  if (run.disabled > 0) parts.push(`${fmt(run.disabled)} not made (disabled)`);
  return `${parts.join(', ')} of ${fmt(run.captured)}`;
}

function DeliveryLog({ siteId, refreshKey, onResent, busy, setBusy }: {
  siteId: string; refreshKey: number; onResent: (w: WebhookDelivery) => void; busy: string | null; setBusy: (b: string | null) => void;
}) {
  const { toast } = useToast();
  const [filter, setFilter] = useState<LogFilter>('all');
  const [rows, setRows] = useState<DeliveryLogEntry[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  // true once "Load more" was used: a refresh must then keep the older pages and their cursor (PR #7228 r4)
  const pagedBeyondFirst = useRef(false);
  // PR #7228 rev. 5.2 r3: how many pages are shown, so a refresh revalidates all of them
  const pagesShown = useRef(1);
  // PR #7228 r7: one "Load more" at a time (the button is disabled while its page loads)
  const [loadingMore, setLoadingMore] = useState(false);
  const loadingMoreRef = useRef(false);

  // ── ONE ordering rule for every delivery-log load (PR #7228 rev. 5.2 r7, replacing the per-filter generation of r5
  // and the page epoch of r6) ──────────────────────────────────────────────────────────────────────────────────────
  //   1. every load — the first, a filter change, a refresh, "Load more" — takes the next number of ONE monotonic
  //      sequence, and an answer is applied only if its number is still the LATEST issued: anything older is dropped,
  //      whatever order the answers land in;
  //   2. a refresh never starts while a load is in flight: it is noted, and runs once the in-flight loads have landed
  //      (also when a load started while a refresh was in flight dropped it), so the view always ends current.
  // A filter change needs no pending refresh: it fetches the new list itself.
  const seq = useRef(0);
  const inFlight = useRef(0);
  const refreshPending = useRef(false);
  const loadRef = useRef<(mode: 'reset' | 'more' | 'refresh', from: string | null) => Promise<void>>(async () => {});

  const load = useCallback(async (mode: 'reset' | 'more' | 'refresh', from: string | null): Promise<void> => {
    if (mode === 'refresh' && inFlight.current > 0) {
      refreshPending.current = true;
      return;
    }
    if (mode === 'more') {
      if (loadingMoreRef.current) return;
      loadingMoreRef.current = true;
      setLoadingMore(true);
    }
    if (mode === 'reset') refreshPending.current = false;
    else if (inFlight.current > 0) refreshPending.current = true; // this load drops whatever is in flight: refresh after
    const mine = ++seq.current;
    const current = () => mounted.current && mine === seq.current;
    inFlight.current += 1;
    try {
      if (mode === 'refresh' && pagedBeyondFirst.current) {
        // PR #7228 rev. 5.2 r3/r5: every page shown is fetched again, from the first, and replaces the list. A filtered
        // list (Failed, Held, Skipped) is a membership the server decides — a product that recovered LEAVES it (an empty
        // first page empties it); and in "All" a call shown on an older page changes status (Sending → Delivered)
        // without ever reaching the refreshed first page. Merging only the first page in kept both stale.
        const walked: DeliveryLogEntry[] = [];
        const seen = new Set<string>();
        let next: string | null = null;
        let pages = 0;
        do {
          const p = await webhookDeliveryApi.deliveries(siteId, { filter, cursor: next });
          if (!current()) return;
          for (const d of p.deliveries) if (!seen.has(d.deliveryId)) { seen.add(d.deliveryId); walked.push(d); }
          next = p.nextCursor;
          pages += 1;
        } while (next && pages < pagesShown.current);
        pagesShown.current = Math.max(1, pages);
        pagedBeyondFirst.current = pages > 1;
        setRows(walked);
        setCursor(next);
        setError(null);
        return;
      }
      const page = await webhookDeliveryApi.deliveries(siteId, { filter, cursor: from });
      if (!current()) return;
      if (mode === 'more') {
        pagedBeyondFirst.current = true;
        pagesShown.current += 1;
        setRows((prev) => {
          // a row can move between pages while the user reads (its call changed): never show it twice
          const shown = new Set((prev || []).map((d) => d.deliveryId));
          return [...(prev || []), ...page.deliveries.filter((d) => !shown.has(d.deliveryId))];
        });
        setCursor(page.nextCursor);
      } else {
        pagedBeyondFirst.current = false;
        pagesShown.current = 1;
        setRows(page.deliveries);
        setCursor(page.nextCursor);
      }
      setError(null);
    } catch (err: any) {
      if (current()) setError(err?.message || String(err));
    } finally {
      inFlight.current -= 1;
      if (mode === 'more') {
        loadingMoreRef.current = false;
        if (mounted.current) setLoadingMore(false);
      }
      if (inFlight.current === 0 && refreshPending.current && mounted.current) {
        refreshPending.current = false;
        void loadRef.current('refresh', null); // the current filter's load (a filter change re-creates it)
      }
    }
  }, [siteId, filter]);
  loadRef.current = load;

  useEffect(() => { load('reset', null); }, [load]);
  const firstRefresh = useRef(true);
  useEffect(() => {
    if (firstRefresh.current) { firstRefresh.current = false; return; }
    load('refresh', null);
  }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const resend = async (d: DeliveryLogEntry) => {
    setBusy(`resend-${d.deliveryId}`);
    try {
      const { webhook } = await webhookDeliveryApi.resend(siteId, d.deliveryId);
      if (!mounted.current) return;
      toast({ title: `Resend queued for ${d.productId}` });
      onResent(webhook);
      load('refresh', null);
    } catch (err: any) {
      if (mounted.current) toast({ title: 'Webhook', description: err?.message || String(err), variant: 'destructive' });
    } finally {
      if (mounted.current) setBusy(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Delivery log</CardTitle>
        <CardDescription>The last calls, newest first. Resend sends the product&apos;s current content. Resend and the test product send the product you choose, in any mode. With canonicals only, a resent variant carries the same generated content as its model; only its own id, GTIN and price differ.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2" role="group" aria-label="Filter">
          {/* PRD rev. 5.2 "Delivery log": filter by failed, held or skipped */}
          {LOG_FILTERS.map(([f, label]) => (
            <Button key={f} size="sm" variant={filter === f ? 'default' : 'outline'} aria-pressed={filter === f} onClick={() => setFilter(f)}>{label}</Button>
          ))}
        </div>
        {error && <p className="text-sm text-destructive">Could not load the delivery log: {error}</p>}
        {rows && rows.length === 0 && <p className="text-sm text-muted-foreground">{EMPTY_LOG[filter]}</p>}
        {rows && rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="delivery-log">
              <thead>
                <tr className="text-left text-muted-foreground">
                  <th className="py-1 pr-3">Time</th>
                  <th className="py-1 pr-3">Product</th>
                  <th className="py-1 pr-3">Run</th>
                  <th className="py-1 pr-3">Status</th>
                  <th className="py-1 pr-3">HTTP</th>
                  <th className="py-1 pr-3">Duration</th>
                  <th className="py-1 pr-3">Attempt</th>
                  <th className="py-1" />
                </tr>
              </thead>
              <tbody>
                {rows.map((d) => (
                  <tr key={d.deliveryId} className="border-t align-top">
                    <td className="py-1 pr-3 whitespace-nowrap">{formatDate(d.lastCallAt || d.updatedAt)}</td>
                    <td className="py-1 pr-3 font-mono">{d.productId}</td>
                    <td className="py-1 pr-3 whitespace-nowrap">{d.runKind === 'full_export' ? 'full export' : 'changes'} #{d.runSeq}</td>
                    <td className="py-1 pr-3">{callStatusText(d)}</td>
                    <td className="py-1 pr-3">{d.httpStatus ?? '—'}</td>
                    <td className="py-1 pr-3">{d.durationMs === null || d.durationMs === undefined ? '—' : `${fmt(d.durationMs)} ms`}</td>
                    <td className="py-1 pr-3">{d.calls || '—'}</td>
                    <td className="py-1">
                      {d.canResend && (
                        <Button size="sm" variant="outline" disabled={busy !== null} onClick={() => resend(d)} aria-label={`Resend ${d.productId}`}>Resend</Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {cursor && <Button size="sm" variant="outline" disabled={loadingMore} onClick={() => load('more', cursor)}>{loadingMore ? 'Loading…' : 'Load more'}</Button>}
      </CardContent>
    </Card>
  );
}

/** One download button with its failure shown under it (PRD rev. 5.3: the sample's 404 and 409 sentences inline). */
function DownloadButton({ label, failure, download, children }: {
  label: string; failure: string; download: () => Promise<void>; children?: React.ReactNode;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      await download();
    } catch (err: any) {
      setError(err?.message || String(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" disabled={busy} onClick={run}>
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}{label}
        </Button>
        {children}
      </div>
      {error && <p className="text-sm text-destructive" role="alert">{`${failure}: ${error}`}</p>}
    </div>
  );
}

/**
 * "Download JSON schema" (PRD rev. 5.3): the file the dispatcher validates with (GET /webhook-delivery/schema, through
 * authFetch). The integration guide and the API Documentation tab use this one button, so the channels point at one
 * contract file.
 */
export function SchemaDownloadButton({ siteId, children }: { siteId: string; children?: React.ReactNode }) {
  return (
    <DownloadButton label="Download JSON schema" failure="Could not download the JSON schema"
      download={async () => saveFile(await webhookDeliveryApi.schema(siteId), SCHEMA_FILE_NAME, 'application/schema+json')}>
      {children}
    </DownloadButton>
  );
}

/**
 * The integration guide (PRD rev. 5.3, AC17-AC19): the six parts built from the status `limits` and `schema`, at the top
 * of the tab. Open while the webhook is not enabled; collapsed (one click opens it) once it is. "Download integration
 * guide" saves the same structured source as Markdown.
 */
function IntegrationGuideCard({ siteId, storeName, webhook, identity }: {
  siteId: string; storeName: string; webhook: WebhookDelivery; identity: { value: GuideIdentity | null; error: string | null };
}) {
  const enabled = webhook.status === 'active' || webhook.status === 'paused';
  const [open, setOpen] = useState(!enabled);
  const store = { siteId, name: storeName };
  const guide = buildIntegrationGuide(webhook.limits, store, webhook.schema, identity.value);
  const bodyId = `webhook-guide-${siteId}`;
  return (
    <Card data-testid="integration-guide">
      <CardHeader>
        <CardTitle>Integration guide</CardTitle>
        <CardDescription>What your endpoint must do, what PageMind sends and how it reads your answers. Share it with the engineer who builds your endpoint.</CardDescription>
        <div className="flex flex-wrap gap-2 pt-2">
          <Button size="sm" variant="outline" aria-expanded={open} aria-controls={bodyId} onClick={() => setOpen((o) => !o)}>
            {open ? 'Hide the integration guide' : 'Show the integration guide'}
          </Button>
          <Button size="sm" variant="outline" onClick={() => saveFile(renderGuideMarkdown(guide), guideFileName(store), 'text/markdown')}>
            <Download className="h-4 w-4 mr-2" />Download integration guide
          </Button>
        </div>
      </CardHeader>
      {open && (
        <CardContent id={bodyId} className="space-y-5 text-sm">
          {identity.error && (
            <p className="text-sm text-destructive" role="alert">{`Could not load this store's sample product for the header examples, so they are placeholders: ${identity.error}`}</p>
          )}
          {guide.parts.map((part, i) => (
            <section key={part.title} className="space-y-2">
              <h3 className="font-semibold">{part.title}</h3>
              {part.blocks.map((block, j) => (block.kind === 'paragraph' ? (
                <p key={j}>{block.text}</p>
              ) : (
                <div key={j} className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-muted-foreground">{block.columns.map((c) => <th key={c} className="py-1 pr-3">{c}</th>)}</tr>
                    </thead>
                    <tbody>
                      {block.rows.map((row, k) => (
                        <tr key={k} className="border-t align-top">
                          {row.map((c, m) => <td key={m} className={`py-1 pr-3${m === 0 ? ' font-medium' : ''}`}>{c}</td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )))}
              {/* part 2, "What we send": the schema the body follows, and one product of this store as a call carries it */}
              {i === 1 && (
                <div className="space-y-2">
                  <SchemaDownloadButton siteId={siteId}>
                    <span className="text-xs text-muted-foreground break-all">{schemaLabel(webhook.schema)}</span>
                  </SchemaDownloadButton>
                  <DownloadButton label="Download a sample product" failure="Could not download a sample product"
                    download={async () => {
                      const sample = await webhookDeliveryApi.sample(siteId);
                      saveFile(sample.bytes, `pagemind-sample-${sample.sku}.json`, 'application/json');
                    }} />
                </div>
              )}
            </section>
          ))}
        </CardContent>
      )}
    </Card>
  );
}

/**
 * Keyed by site: switching sites mounts a fresh tab, so a late answer for the previous site never lands in the new
 * site's view (the bucket tab's rule, codex review of #6944).
 */
export function WebhookTab({ siteId, storeName }: { siteId: string; storeName: string }) {
  return <SiteWebhookTab key={siteId} siteId={siteId} storeName={storeName} />;
}

function SiteWebhookTab({ siteId, storeName }: { siteId: string; storeName: string }) {
  const { toast } = useToast();
  const { user } = useAuth();
  const isOperator = user?.role === 'super_admin';
  const [webhook, setWebhook] = useState<WebhookDelivery | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [testError, setTestError] = useState<string | null>(null); // the last Send test product refusal, shown inline
  const [url, setUrl] = useState('');
  const [auth, setAuth] = useState<AuthInput>({ type: 'header', name: 'Authorization', value: '' });
  const [canonicalsOnly, setCanonicalsOnly] = useState(false); // rev. 5.6: the Enable form's checkbox, off by default
  const [editing, setEditing] = useState<'url' | 'auth' | null>(null);
  const [pace, setPace] = useState<{ maxParallel: string; maxPerMinute: string } | null>(null);
  const [timeoutTyped, setTimeoutTyped] = useState<string | null>(null); // rev. 5.7: the user's draft, else the saved value
  const [timeoutError, setTimeoutError] = useState<string | null>(null); // the last refusal of Save timeout, shown in the row
  const [logKey, setLogKey] = useState(0);
  const loadAbort = useRef<AbortController | null>(null);
  const epoch = useRef(0);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  const load = useCallback(async () => {
    loadAbort.current?.abort();
    const controller = new AbortController();
    loadAbort.current = controller;
    const startedAt = epoch.current;
    try {
      const { webhook: next } = await webhookDeliveryApi.get(siteId, controller.signal);
      if (controller.signal.aborted || epoch.current !== startedAt) return;
      setWebhook(next);
      setLoadError(null);
    } catch (err: any) {
      if (err?.name === 'AbortError' || epoch.current !== startedAt) return;
      setLoadError(err?.message || String(err));
    }
  }, [siteId]);

  useEffect(() => {
    load();
    return () => loadAbort.current?.abort();
  }, [load]);
  // PRD rev. 5.4: the guide's header examples are this store's own sample product (this component is keyed by site); a
  // store with no exportable product (404) gets the neutral placeholders. Rev. 5.6 (PR #7309 round 3): fetched again
  // whenever the endpoint's configuration changes (enabled or not, canonicals only or not), so the X-PageMind-Granularity
  // example and the Markdown download follow Enable / Disable without a reload; a status poll alone changes neither.
  const [identity, setIdentity] = useState<{ value: GuideIdentity | null; error: string | null }>({ value: null, error: null });
  const endpointEnabled = webhook?.status === 'active' || webhook?.status === 'paused';
  const endpointCanonicalsOnly = endpointEnabled && webhook?.canonicalsOnly === true;
  useEffect(() => {
    let live = true;
    webhookDeliveryApi.sampleIdentity(siteId).then(
      (value) => { if (live) setIdentity({ value, error: null }); },
      (err: any) => {
        if (!live) return;
        const none = err instanceof WebhookApiError && err.status === 404 && err.code === 'NO_EXPORTABLE_PRODUCT';
        setIdentity({ value: null, error: none ? null : (err?.message || String(err)) });
      },
    );
    return () => { live = false; };
  }, [siteId, endpointEnabled, endpointCanonicalsOnly]);

  const delay = pollDelay(webhook);
  useEffect(() => {
    if (delay === null) return;
    const interval = setInterval(() => { load(); setLogKey((k) => k + 1); }, delay);
    return () => clearInterval(interval);
  }, [delay, load]);


  /** One mutation: its answer is the new status; a refusal shows the server's sentence. */
  const act = async (name: string, fn: () => Promise<{ webhook: WebhookDelivery }>,
    { success, inline = false, onError }: { success?: string; inline?: boolean; onError?: (message: string) => void } = {}) => {
    setBusy(name);
    setFormError(null);
    epoch.current += 1;
    loadAbort.current?.abort();
    try {
      const { webhook: next } = await fn();
      epoch.current += 1;
      if (!mounted.current) return false;
      setWebhook(next);
      setLoadError(null);
      setLogKey((k) => k + 1);
      if (success) toast({ title: success });
      return true;
    } catch (err: any) {
      epoch.current += 1;
      if (!mounted.current) return false;
      const message = err instanceof WebhookApiError || err?.message ? err.message : String(err);
      if (onError) onError(message);
      else if (inline) setFormError(message);
      else toast({ title: 'Webhook', description: message, variant: 'destructive' });
      return false;
    } finally {
      if (mounted.current) setBusy(null);
    }
  };

  if (loadError && !webhook) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Could not load the webhook settings: {loadError}{' '}
          <Button variant="link" className="h-auto p-0" onClick={load}>Try again</Button>
        </AlertDescription>
      </Alert>
    );
  }
  if (!webhook) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground p-6">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }

  const refreshError = loadError ? (
    <Alert variant="destructive">
      <AlertCircle className="h-4 w-4" />
      <AlertDescription>
        Could not refresh the webhook status: {loadError}. What is shown below may be out of date.{' '}
        <Button variant="link" className="h-auto p-0" onClick={load}>Try again</Button>
      </AlertDescription>
    </Alert>
  ) : null;

  // ── not enabled, or disabled: the enable form ──────────────────────────────────────────────────────────────────
  if (webhook.status === 'not_enabled' || webhook.status === 'disabled') {
    return (
      <div className="space-y-4">
        {refreshError}
        {/* keyed by state: enabling mounts it again, collapsed (PRD rev. 5.3) */}
        <IntegrationGuideCard key="not-enabled" siteId={siteId} storeName={storeName} webhook={webhook} identity={identity} />
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Webhook className="h-5 w-5" /> Webhook</CardTitle>
            <CardDescription>{WHAT_YOU_GET}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {webhook.status === 'disabled' && (
              <p className="text-sm text-muted-foreground">The webhook was turned off on {formatDate(webhook.disabledAt)}. Its stored authentication was deleted.</p>
            )}
            <div className="space-y-1">
              <Label htmlFor="webhook-url">Endpoint URL</Label>
              <Input id="webhook-url" value={url} placeholder="https://your-endpoint.example/pagemind" onChange={(e) => setUrl(e.target.value)} />
              <p className="text-xs text-muted-foreground">HTTPS only. Your endpoint must answer within {webhook.limits.requestTimeoutSeconds} s and accept bodies up to about 1.3 MB.</p>
            </div>
            <div className="space-y-1">
              <Label>Authentication</Label>
              <AuthFields value={auth} onChange={setAuth} idPrefix="enable-auth" />
            </div>
            {webhook.canonicalsAvailable && (
              <div className="space-y-1">
                <label className="flex items-center gap-2 text-sm font-medium">
                  <input type="checkbox" checked={canonicalsOnly} onChange={(e) => setCanonicalsOnly(e.target.checked)} />
                  {CANONICALS_LABEL}
                </label>
                <p className="text-xs text-muted-foreground">{CANONICALS_HELP}</p>
              </div>
            )}
            {formError && <p className="text-sm text-destructive" role="alert">{formError}</p>}
            <p className="text-sm text-muted-foreground">Nothing is sent when you enable. Changes are sent from that moment on; products that exist today are sent only by a full export.</p>
            <Button disabled={busy !== null} onClick={async () => {
              // rev. 5.6: the option is sent only where it exists (left out, the backend reads false)
              const option = webhook.canonicalsAvailable ? canonicalsOnly : undefined;
              if (await act('enable', () => webhookDeliveryApi.enable(siteId, url.trim(), auth, option), { inline: true, success: 'Webhook enabled' })) {
                setAuth({ type: 'header', name: 'Authorization', value: '' });
              }
            }}>
              {busy === 'enable' && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Enable webhook
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── enabled (active or paused) ─────────────────────────────────────────────────────────────────────────────────
  const q = webhook.queue!;
  const fe = webhook.fullExport!;
  // The pace inputs show the user's draft once they edit, else the saved pace — in the SAME render as the rest of the
  // status (PR #7228: they used to come from an effect one commit later, which a loaded CI run could observe as absent).
  const paceView = pace ?? (webhook.pace ? { maxParallel: String(webhook.pace.maxParallel), maxPerMinute: String(webhook.pace.maxPerMinute) } : null);
  // rev. 5.7: as the pace, the draft once the user edits, else the saved value — in the same render as the status
  const timeoutView = timeoutTyped ?? (webhook.requestTimeoutSeconds !== undefined ? String(webhook.requestTimeoutSeconds) : null);
  const { requestTimeoutMinSeconds: timeoutMin, requestTimeoutMaxSeconds: timeoutMax } = webhook.limits;
  const timeoutInvalid = timeoutView !== null && !timeoutValid(timeoutView, timeoutMin, timeoutMax);
  const run = fe.run;
  const runActive = Boolean(run && ['queued', 'running', 'failed_retryable'].includes(run.status));
  const circuitOpen = webhook.status === 'active' && webhook.circuit?.state === 'open';
  const paused = pauseSentence(webhook);
  const autoPaused = webhook.status === 'paused' && webhook.pause && webhook.pause.reason !== 'user';
  const showOperatorLink = isOperator && (autoPaused || circuitOpen || q.held > 0 || q.failed > 0 || (run && run.status === 'failed') || (webhook.lastRound && webhook.lastRound.status === 'failed'));
  const test = webhook.lastTest;
  const held = webhook.held;
  const testPending = Boolean(test && (test.status === 'pending' || test.status === 'in_flight'));
  const unit = unitOf(webhook.canonicalsOnly); // rev. 5.6: "models" in counts when the option is on

  let fullExportText: string;
  if (fe.waiting) fullExportText = 'Waiting: the full export starts right after the current hourly changes.';
  else if (fe.requestedAt) fullExportText = 'Requested: the full export starts within a minute.';
  else if (run && runActive) fullExportText = `Full export in progress: ${progressText(run)}.`;
  else if (run && run.status === 'completed') fullExportText = `Last full export: ${progressText(run)} (${unit(2)} queued ${formatDate(run.finishedAt)}).`;
  else if (run && run.status === 'failed') fullExportText = `The last full export failed: ${run.error || 'unknown error'}. You can request a new one.`;
  else fullExportText = 'No full export yet. Products that exist today are sent only by a full export.';
  const fullExportBlocked = fe.waiting || Boolean(fe.requestedAt) || runActive || Boolean(fe.allowedAt);

  const row = (label: string, control: React.ReactNode, note?: React.ReactNode) => (
    <tr className="border-t align-top">
      <th scope="row" className="py-3 pr-4 text-left font-medium whitespace-nowrap">{label}</th>
      <td className="py-3 space-y-1">{control}{note && <div className="text-xs text-muted-foreground">{note}</div>}</td>
    </tr>
  );

  return (
    <div className="space-y-4">
      {refreshError}
      <IntegrationGuideCard key="enabled" siteId={siteId} storeName={storeName} webhook={webhook} identity={identity} />
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Webhook className="h-5 w-5" /> Webhook
            <Badge variant={webhook.status === 'active' ? 'default' : 'secondary'}>{webhook.status === 'active' ? 'Active' : 'Paused'}</Badge>
          </CardTitle>
          <CardDescription>{WHAT_YOU_GET}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {paused && (
            <Alert variant={autoPaused ? 'destructive' : 'default'}>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{paused}</AlertDescription>
            </Alert>
          )}
          {circuitOpen && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Your endpoint is failing (connection errors, timeouts or server errors). Calls are on hold; we try one call at {formatDate(webhook.circuit?.nextProbeAt)}. Nothing is lost.
              </AlertDescription>
            </Alert>
          )}
          {webhook.retryAfterUntil && (
            <p className="text-sm">Your endpoint asked us to wait (Retry-After). The next call goes at {formatDate(webhook.retryAfterUntil)}.</p>
          )}
          {(q.held > 0 || q.failed > 0) && (
            <p className="text-sm text-destructive">
              {q.held > 0 ? `${fmt(q.held)} ${unit(q.held)} held: ${q.held === 1 ? 'it' : 'they'} did not pass the payload contract check and ${q.held === 1 ? 'was' : 'were'} not sent. ` : ''}
              {q.failed > 0 ? `${fmt(q.failed)} call${q.failed === 1 ? '' : 's'} failed: your endpoint rejected ${q.failed === 1 ? 'it' : 'them'}. ` : ''}
              See the delivery log below.
            </p>
          )}
          {showOperatorLink && (
            <p className="text-sm" data-testid="operator-link">
              Operators: this is also in the <a className="text-blue-600 underline" href="/admin/ops-console">Ops Console</a>.
            </p>
          )}
          <dl className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
            <div><dt className="text-muted-foreground">Last successful call</dt><dd>{webhook.lastSuccessAt ? formatDate(webhook.lastSuccessAt) : 'None yet'}</dd></div>
            <div><dt className="text-muted-foreground">Failures in the last 24 h</dt><dd>{fmt(webhook.failuresLast24h)}</dd></div>
            <div><dt className="text-muted-foreground">Next hourly changes</dt><dd>{formatDate(webhook.nextRoundAt)}</dd></div>
            <div>
              <dt className="text-muted-foreground">Circuit</dt>
              <dd data-testid="circuit-state">{webhook.circuit?.state === 'open'
                ? `Open since ${formatDate(webhook.circuit.openedAt)} · next try ${formatDate(webhook.circuit.nextProbeAt)}`
                : 'Closed (calls go out)'}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Queue</dt>
              <dd>{q.pending + q.inFlight === 0 ? 'Nothing waiting.' : `${fmt(q.pending)} waiting · ${fmt(q.inFlight)} sending · about ${fmt(q.estimatedMinutesToEmpty)} min at the current pace`}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Configuration</CardTitle>
        </CardHeader>
        <CardContent>
          <table className="w-full text-sm" data-testid="webhook-configuration">
            <tbody>
              {row('Endpoint URL', editing === 'url' ? (
                <div className="flex gap-2">
                  <Input aria-label="New endpoint URL" value={url} onChange={(e) => setUrl(e.target.value)} />
                  <Button size="sm" disabled={busy !== null} onClick={async () => { if (await act('url', () => webhookDeliveryApi.updateUrl(siteId, url.trim()), { inline: true, success: 'Endpoint URL changed' })) setEditing(null); }}>Save</Button>
                  <Button size="sm" variant="ghost" onClick={() => { setEditing(null); setFormError(null); }}>Cancel</Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <code className="break-all">{webhook.url}</code>
                  <Button size="sm" variant="outline" onClick={() => { setUrl(webhook.url || ''); setEditing('url'); setFormError(null); }}>Change</Button>
                </div>
              ), 'A change applies to the next call.')}
              {row('Authentication', editing === 'auth' ? (
                <div className="space-y-2">
                  <AuthFields value={auth} onChange={setAuth} idPrefix="replace-auth" />
                  <div className="flex gap-2">
                    <Button size="sm" disabled={busy !== null} onClick={async () => {
                      if (await act('auth', () => webhookDeliveryApi.replaceAuth(siteId, auth), { inline: true, success: 'Authentication replaced' })) {
                        setEditing(null);
                        setAuth({ type: 'header', name: 'Authorization', value: '' });
                      }
                    }}>Save</Button>
                    <Button size="sm" variant="ghost" onClick={() => { setEditing(null); setFormError(null); }}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <code>{webhook.auth ? `${webhook.auth.headerName}: ${webhook.auth.masked}` : '—'}</code>
                  <Button size="sm" variant="outline" onClick={() => { setEditing('auth'); setFormError(null); }}>Replace</Button>
                </div>
              ), 'The new value applies to the next call.')}
              {/* rev. 5.6 part 1: read-only — a change is Disable + Enable, so a queue never mixes the two meanings */}
              {row('Granularity', <span>{granularityText(webhook.canonicalsOnly)}</span>, 'Set at Enable. To change it, disable the webhook and enable it again.')}
              {formError && editing && (
                <tr><td colSpan={2}><p className="text-sm text-destructive" role="alert">{formError}</p></td></tr>
              )}
              {row('Send test product', (
                <div className="space-y-1">
                  <Button size="sm" variant="outline" disabled={busy !== null || webhook.status !== 'active' || testPending}
                    onClick={() => { setTestError(null); act('test', () => webhookDeliveryApi.sendTest(siteId), { success: 'Test product queued', onError: setTestError }); }}>
                    <Send className="h-4 w-4 mr-2" />Send test product
                  </Button>
                  {/* PR #7309 round 2: a refusal (e.g. 409 ENDPOINT_CHANGED) is shown here, in the server's words */}
                  {testError && <p className="text-sm text-destructive" role="alert">{testError}</p>}
                  {/* PR #7228 r7: the latest ANSWER is shown whenever there is one — a test queued again after a 401, 429
                      or 5xx keeps showing what the endpoint said, with what happens next */}
                  {test && (test.outcome ? (
                    <div className="text-sm space-y-1">
                      <p>Last test: {test.productId} — {test.httpStatus !== null ? `HTTP ${test.httpStatus}` : `no HTTP answer (${test.error || 'no response'})`} in {fmt(test.durationMs)} ms ({formatDate(test.at)}).</p>
                      {test.response && <pre className="bg-muted rounded p-2 text-xs whitespace-pre-wrap break-all" aria-label="Test response">{test.response}</pre>}
                      {test.status === 'pending' && (
                        <p className="text-muted-foreground">{webhook.status === 'paused' ? 'It is queued again and goes out when you resume.' : 'It is queued again and retried automatically.'}</p>
                      )}
                      {test.inFlight && <p className="text-muted-foreground">Sending it again now…</p>}
                    </div>
                  ) : testPending ? (
                    <p className="text-sm">Sending {test.productId}…</p>
                  ) : (
                    <p className="text-sm text-destructive">Last test: {test.productId} was not sent: {test.error || test.status}.</p>
                  ))}
                </div>
              ), 'Sends one real product now and shows the answer of your endpoint (the first 500 characters).')}
              {row('Full export', (
                <div className="space-y-1">
                  <Button size="sm" variant="outline" disabled={busy !== null || fullExportBlocked}
                    onClick={() => act('full', () => webhookDeliveryApi.requestFullExport(siteId), { success: 'Full export requested' })}>
                    Request full export
                  </Button>
                  <p className="text-sm">{fullExportText}</p>
                  {fe.allowedAt && <p className="text-sm text-muted-foreground">Available again at {formatDate(fe.allowedAt)}.</p>}
                </div>
              ), `Sends every product of this store, one call each. At most one every ${webhook.limits.fullSyncMinIntervalHours} hours.`)}
              {row('Re-check held', (
                <div className="space-y-1">
                  <Button size="sm" variant="outline" disabled={busy !== null || !held || held.total === 0}
                    onClick={() => act('recheck', () => webhookDeliveryApi.recheckHeld(siteId), { success: 'Held products queued for a re-check' })}>
                    Re-check held
                  </Button>
                  <p className="text-sm">{!held || held.total === 0 ? `No ${unit(1)} is held.` : `${fmt(held.total)} ${unit(held.total)} held by the payload contract check:`}</p>
                  {held && held.groups.length > 0 && (
                    <ul className="text-xs text-muted-foreground list-disc pl-5" aria-label="Held groups">
                      {held.groups.map((g) => <li key={g.path}><code>{g.path}</code>: {fmt(g.count)}</li>)}
                    </ul>
                  )}
                </div>
              ), 'Checks every held product again (after the defect is fixed at its source) and sends those that pass. A new schema revision does this on its own.')}
              {row('Pause / Resume', webhook.status === 'active' ? (
                <Button size="sm" variant="outline" disabled={busy !== null} onClick={() => act('pause', () => webhookDeliveryApi.pause(siteId), { success: 'Webhook paused' })}>Pause</Button>
              ) : (
                <Button size="sm" disabled={busy !== null} onClick={() => act('resume', () => webhookDeliveryApi.resume(siteId), { success: 'Webhook resumed' })}>Resume</Button>
              ), 'While paused, changes keep being queued, one call per product with its latest version; nothing is skipped.')}
              {row('Pace', paceView && (
                <div className="flex flex-wrap items-end gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="pace-parallel">Max calls in parallel</Label>
                    <Input id="pace-parallel" className="w-28" type="number" min={webhook.limits.maxParallel.min} max={webhook.limits.maxParallel.max}
                      value={paceView.maxParallel} onChange={(e) => setPace({ ...paceView, maxParallel: e.target.value })} />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="pace-minute">Max calls per minute</Label>
                    <Input id="pace-minute" className="w-28" type="number" min={webhook.limits.maxPerMinute.min} max={webhook.limits.maxPerMinute.max}
                      value={paceView.maxPerMinute} onChange={(e) => setPace({ ...paceView, maxPerMinute: e.target.value })} />
                  </div>
                  <Button size="sm" variant="outline" disabled={busy !== null}
                    onClick={async () => { if (await act('pace', () => webhookDeliveryApi.setPace(siteId, Number(paceView.maxParallel), Number(paceView.maxPerMinute)), { success: 'Pace saved' })) setPace(null); }}>
                    Save pace
                  </Button>
                </div>
              ), `Set what your endpoint can take (${webhook.limits.maxParallel.min}–${webhook.limits.maxParallel.max} in parallel, default ${webhook.limits.maxParallel.default}; default ${webhook.limits.maxPerMinute.default} per minute).`)}
              {row('Response timeout', timeoutView !== null && (
                <div className="space-y-1">
                  <div className="flex flex-wrap items-end gap-3">
                    <div className="space-y-1">
                      <Label htmlFor="request-timeout">Response timeout (seconds)</Label>
                      <Input id="request-timeout" className="w-28" type="number" min={timeoutMin} max={timeoutMax} step={1}
                        aria-invalid={timeoutInvalid} value={timeoutView}
                        onChange={(e) => { setTimeoutTyped(e.target.value); setTimeoutError(null); }} />
                    </div>
                    <Button size="sm" variant="outline" disabled={busy !== null || timeoutInvalid}
                      onClick={async () => {
                        setTimeoutError(null);
                        if (await act('timeout', () => webhookDeliveryApi.setTimeout(siteId, Number(timeoutView)), { success: 'Response timeout saved', onError: setTimeoutError })) setTimeoutTyped(null);
                      }}>
                      Save timeout
                    </Button>
                  </div>
                  {timeoutInvalid && <p className="text-sm text-destructive" role="alert">{timeoutBounds(timeoutMin, timeoutMax)}</p>}
                  {!timeoutInvalid && timeoutError && <p className="text-sm text-destructive" role="alert">{timeoutError}</p>}
                </div>
              ), timeoutHelp(timeoutMin, timeoutMax))}
              {row('Compression', (
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={Boolean(webhook.gzip)} disabled={busy !== null}
                    onChange={(e) => act('gzip', () => webhookDeliveryApi.setGzip(siteId, e.target.checked))} />
                  Send calls gzipped (Content-Encoding: gzip)
                </label>
              ), 'Only if your endpoint decompresses request bodies. Off by default.')}
              {row('Disable', (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="destructive" disabled={busy !== null}>Disable webhook</Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Disable the webhook?</AlertDialogTitle>
                      <AlertDialogDescription>Calls stop and the stored authentication is deleted. Calls still queued are not made (closed as disabled). Changes made while disabled need a full export after you enable again.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => act('disable', () => webhookDeliveryApi.disable(siteId), { success: 'Webhook disabled' })}>Disable</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              ), 'Removes the webhook: calls stop and the stored authentication is deleted.')}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <DeliveryLog siteId={siteId} refreshKey={logKey} busy={busy} setBusy={setBusy} onResent={(w) => setWebhook(w)} />
    </div>
  );
}
